import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';

/**
 * RoleGuard - Enforces Role-Based Access Control (RBAC) on routes.
 * Checks whether the active user (or admin simulation) possesses at least one of
 * the required roles. If not, safely redirects to fallbackPath (default: /dashboard/app).
 */
export default function RoleGuard({ allowedRoles = [], fallbackPath = '/dashboard/app', children }) {
  const { user } = useSelector((state) => state.auth || {});

  const viewAsRole = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('mtc_view_as_role') : null;

  let rolesList = [];
  if (viewAsRole) {
    rolesList = [viewAsRole.toLowerCase().trim()];
  } else {
    let rawRoles = user?.roles || user?.role || user?.rolesList;
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem('user')) || JSON.parse(localStorage.getItem('profile'));
    } catch (e) {
      // ignore JSON parse error
    }
    if (!rawRoles && stored) {
      rawRoles = stored?.roles || stored?.role || stored?.rolesList;
    }
    if (Array.isArray(rawRoles)) {
      rolesList = rawRoles.map((r) => (typeof r === 'string' ? r : r?.name || '').toLowerCase());
    } else if (typeof rawRoles === 'string') {
      rolesList = [rawRoles.toLowerCase()];
    }

    if (user?.isAdmin || stored?.isAdmin) rolesList.push('admin');
    if (user?.isPresident || stored?.isPresident) rolesList.push('president');
    if (user?.isFinancial || stored?.isFinancial) rolesList.push('financial');
    if (user?.isBusiness || stored?.isBusiness) rolesList.push('business');
  }

  // Admin users have full universal access to all routes
  const isAdmin = rolesList.some((r) => r.includes('admin'));
  if (isAdmin) {
    return children ? children : <Outlet />;
  }

  const hasAccess = allowedRoles.some((allowed) =>
    rolesList.some((userRole) => userRole.includes(allowed.toLowerCase().trim()))
  );

  if (!hasAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children ? children : <Outlet />;
}
